/**
 * ExpauseSheet Component
 *
 * A versatile sheet component that adapts based on device orientation:
 * - Portrait mode: Centered dialog (custom implementation)
 * - Landscape mode: Centered dialog (custom implementation)
 *
 * Note: Both modes currently show centered dialogs. Bottom sheet with drag gestures
 * will be implemented later as it requires more complex gesture handling.
 *
 * Supports page-scoped sheets via container prop - sheets render into a page's
 * container and automatically hide/show with the page's visibility.
 *
 * Usage:
 * 1. Wrap your app with <ExpauseSheetProvider>
 * 2. Call ExpauseSheet.show({ content, cta, container, ... })
 *
 * @example
 * ExpauseSheet.show({
 *   title: 'User Actions',
 *   content: <UserActionsContent />,
 *   container: pageContainerRef.current, // Optional: for page-scoped sheets
 *   cta: {
 *     buttons: [
 *       { text: 'Report', onClick: () => {} },
 *       { text: 'Block', onClick: () => {}, variant: 'danger' }
 *     ],
 *     orientation: 'column'
 *   }
 * });
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { SHEET_CONSTANTS } from '@/presentation/constants/sheetConstants';
import { setBodyScrollLock } from '@/presentation/utils/bodyScrollLock';
import { ExpauseSheetContainer } from '../../service/components/ExpauseSheetContainer';
import type {
  ExpauseSheetConfig,
  ExpauseSheetContextValue,
  SheetState,
} from '../config/ExpauseSheet.types';

/**
 * Imperative API for showing sheets
 *
 * Usage:
 * ExpauseSheet.show({
 *   content: <YourComponent />,
 *   title: 'Sheet Title',
 *   cta: { buttons: [...] }
 * });
 */
let globalShowSheet: ((config: ExpauseSheetConfig) => void) | null = null;
let globalHideSheet: (() => void) | null = null;
let globalHideAllSheets: (() => void) | null = null;

export const ExpauseSheet = {
  /**
   * Show a new sheet (adds to stack)
   */
  show: (config: ExpauseSheetConfig) => {
    if (!globalShowSheet) {
      console.error('ExpauseSheet: Provider not found. Wrap your app with <ExpauseSheetProvider>');
      return;
    }
    globalShowSheet(config);
  },

  /**
   * Close the topmost sheet
   */
  close: () => {
    if (!globalHideSheet) {
      console.error('ExpauseSheet: Provider not found. Wrap your app with <ExpauseSheetProvider>');
      return;
    }
    globalHideSheet();
  },

  /**
   * Close all sheets in the stack
   */
  closeAll: () => {
    if (!globalHideAllSheets) {
      console.error('ExpauseSheet: Provider not found. Wrap your app with <ExpauseSheetProvider>');
      return;
    }
    globalHideAllSheets();
  },
};

/**
 * Hook to initialize the global functions
 * This is called automatically by the provider
 */
export const useInitExpauseSheet = () => {
  const { showSheet, hideSheet, hideAllSheets } = useExpauseSheetContext();

  React.useEffect(() => {
    globalShowSheet = showSheet;
    globalHideSheet = hideSheet;
    globalHideAllSheets = hideAllSheets;
    return () => {
      globalShowSheet = null;
      globalHideSheet = null;
      globalHideAllSheets = null;
    };
  }, [showSheet, hideSheet, hideAllSheets]);
};

/**
 * Context for ExpauseSheet
 */
const ExpauseSheetContext = createContext<ExpauseSheetContextValue | null>(null);

/**
 * Hook to access ExpauseSheet context
 * Exported for use by child components (Container, Views)
 */
export const useExpauseSheetContext = (): ExpauseSheetContextValue => {
  const context = useContext(ExpauseSheetContext);
  if (!context) {
    throw new Error('useExpauseSheetContext must be used within ExpauseSheetProvider');
  }
  return context;
};

/**
 * Internal provider component for ExpauseSheet
 * Do not use directly - use ExpauseSheetProvider instead
 */
const ExpauseSheetProviderBase: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sheets, setSheets] = useState<SheetState[]>([]);
  const { currentScreenKeyRef, navigationStack } = useKeepAlive();
  const navigationType = useNavigationType();
  const prevScreenKeyRef = useRef<string | undefined>(currentScreenKeyRef.current);

  // Helper function to manage body overflow and text selection based on sheet state
  const updateBodyOverflow = useCallback((screenKey: string | undefined, allSheets: SheetState[]) => {
    // Check if there are any sheets for the current screen key
    const hasActiveSheets = allSheets.some(sheet => sheet.screenKey === screenKey);
    if (hasActiveSheets) {
      setBodyOverflow();
    } else {
      clearBodyOverflow();
    }
  }, []);

  // Function to set body overflow.
  // Routed through the shared owner-keyed lock (`bodyScrollLock`) instead of
  // writing `document.body.style` directly: `AppOverlayHost` locks the same two
  // globals for its app-level overlays, and an overlay can be raised from
  // inside an open sheet. Owner-keying stops either surface's release from
  // clobbering the other's still-required lock.
  const setBodyOverflow = useCallback(() => {
    setBodyScrollLock('expauseSheet', true); // Also prevents text selection on background content
  }, []);

  // Function to clear body overflow
  const clearBodyOverflow = useCallback(() => {
    setBodyScrollLock('expauseSheet', false);
  }, []);

  // Automatically close sheets when navigating back (POP) if their route is no longer in the stack
  // Keep sheets open when pushing new routes (PUSH) - they'll be hidden by container visibility
  // Also manage body overflow based on navigation and sheet state
  useEffect(() => {
    const currentScreenKey = currentScreenKeyRef.current;
    const prevScreenKey = prevScreenKeyRef.current;

    // Check if screen key changed (route navigation occurred)
    if (currentScreenKey !== prevScreenKey) {
      // On PUSH navigation: Remove overflow and userSelect (we're navigating away from the sheet)
      if (navigationType === 'PUSH') {
        clearBodyOverflow();
      }

      // Only close sheets on POP navigation
      // On PUSH, sheets stay in the stack but are hidden by their container visibility
      if (navigationType === 'POP') {
        // Get all screen keys currently in the navigation stack
        const screenKeysInStack = new Set(
          navigationStack.map((cacheKey) => {
            // Extract screen key from cache key format: "pathname#screenKey"
            const parts = cacheKey.split('#');
            return parts[1] || '';
          }).filter(Boolean)
        );

        // Close sheets whose screen keys are no longer in the navigation stack
        setSheets((prev) => {
          const sheetsToKeep = prev.filter((sheet) => screenKeysInStack.has(sheet.screenKey || ''));
          const sheetsToClose = prev.filter((sheet) => !screenKeysInStack.has(sheet.screenKey || ''));

          if (sheetsToClose.length > 0) {
            // Call onClose for sheets being closed
            sheetsToClose.forEach((sheet) => {
              if (sheet.onClose) {
                sheet.onClose();
              }
            });
          }

          return sheetsToKeep;
        });

        // After closing sheets, check if current route has any remaining sheets
        // Use setTimeout to ensure state update has completed
        setTimeout(() => {
          setSheets((currentSheets) => {
            updateBodyOverflow(currentScreenKey, currentSheets);
            return currentSheets;
          });
        }, 0);
      }

      prevScreenKeyRef.current = currentScreenKey;
    }
  }, [currentScreenKeyRef.current, navigationType, navigationStack, sheets.length, updateBodyOverflow]);

  const showSheet = useCallback((config: ExpauseSheetConfig) => {
    setSheets((prev) => {
      // Prevent too many stacked sheets
      if (prev.length >= SHEET_CONSTANTS.maxStack) {
        console.warn(`ExpauseSheet: Maximum stack size (${SHEET_CONSTANTS.maxStack}) reached. Cannot open more sheets.`);
        return prev;
      }

      // Add new sheet to stack
      const newSheet: SheetState = {
        ...config,
        isOpen: true,
        id: `sheet-${Date.now()}-${prev.length}`,
      };

      // Apply overflow:hidden and user-select:none when opening a sheet
      setBodyOverflow();

      return [...prev, newSheet];
    });
  }, []);

  const hideSheet = useCallback(() => {
    const currentScreenKey = currentScreenKeyRef.current;

    setSheets((prev) => {
      if (prev.length === 0) return prev;

      // Get the topmost sheet and call its onClose if defined
      const topSheet = prev[prev.length - 1];
      if (topSheet?.onClose) {
        topSheet.onClose();
      }

      // Remove the topmost sheet
      const newSheets = prev.slice(0, -1);

      // Check if there are any remaining sheets for the current screen key
      const hasRemainingSheets = newSheets.some(sheet => sheet.screenKey === currentScreenKey);

      // Remove overflow:hidden and user-select:none if no more sheets for current route
      if (!hasRemainingSheets) {
        clearBodyOverflow();
      }

      return newSheets;
    });
  }, [currentScreenKeyRef]);

  const hideAllSheets = useCallback(() => {
    setSheets((prev) => {
      // Call onClose for all sheets
      prev.forEach((sheet) => {
        if (sheet.onClose) {
          sheet.onClose();
        }
      });

      // Remove overflow:hidden and user-select:none when closing all sheets
      clearBodyOverflow();

      return [];
    });
  }, []);


  return (
    <ExpauseSheetContext.Provider value={{ sheets, showSheet, hideSheet, hideAllSheets }}>
      {children}
      <ExpauseSheetContainer />
    </ExpauseSheetContext.Provider>
  );
};

/**
 * Provider component for ExpauseSheet
 * Must wrap your app to use ExpauseSheet.show()
 */
export const ExpauseSheetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ExpauseSheetProviderBase>
      <InitComponent />
      {children}
    </ExpauseSheetProviderBase>
  );
};

const InitComponent: React.FC = () => {
  useInitExpauseSheet();
  return null;
};
